import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Award } from "lucide-react";
import axios from "axios";
import { ProjectDetail } from "@/components/ProjectDetail";
import { proxyImg } from "@/utils/proxyImg";

const API = `${window.location.origin}/api`;

export const Home = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);

  const fetchProjects = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const response = await axios.get(`${API}/projects?_t=${Date.now()}`, { timeout: 30000 });
      setProjects(response.data);
      setFilteredProjects(response.data);
    } catch (error) {
      const status = error?.response?.status || "No response";
      const detail = error?.response?.data?.detail || error?.message || "Unknown error";
      setFetchError(`${status}: ${detail} (API: ${API}/projects)`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleFilter = (category) => {
    setActiveFilter(category);
    if (category === "all") {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter((p) => p.category === category));
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="hero-wrapper bg-[#0A0A0A] relative" data-testid="hero-section">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_c981175b-e071-40bb-8fdb-7002fb2c2926/artifacts/vrugup8l_ig%20post%20d.jpg"
            alt="FaSaad Mimar Interior Design"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]" />
        </div>
        
        <div className="relative z-10 max-w-screen-2xl mx-auto px-16 lg:px-40 py-32 lg:py-0">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8 items-end min-h-screen">
            <div className="lg:col-span-8 pb-24 lg:pb-32">
              <div className="recognition-badge bg-transparent border-white/20 text-white/70 mb-12 opacity-0 animate-fade-up" style={{animationFillMode: 'forwards'}}>
                <Award size={16} />
                <span>25 Under 25 Global Talents — Innovation 2020</span>
              </div>
              
              <h1 className="hero-title text-white mb-8 opacity-0 animate-fade-up delay-1" style={{animationFillMode: 'forwards'}} data-testid="hero-title">
                We<br />
                Curate<br />
                Voids
              </h1>
              
              <p className="hero-subtitle opacity-0 animate-fade-up delay-2" style={{animationFillMode: 'forwards'}}>
                FaSaad Mimar is an award-winning architecture and design studio 
                based in Istanbul. We approach every project with rigorous research 
                and client analysis, creating spaces that inspire and endure.
              </p>
            </div>
            
            <div className="lg:col-span-4 pb-24 lg:pb-32 hidden lg:block">
              <div className="text-white/50 text-xs tracking-widest uppercase mb-4 opacity-0 animate-fade-up delay-3" style={{animationFillMode: 'forwards'}}>
                Scroll to explore
              </div>
              <div className="w-px h-16 bg-white/20 opacity-0 animate-fade-up delay-4" style={{animationFillMode: 'forwards'}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="section-editorial px-16 lg:px-40 bg-[#F5F5F3]" data-testid="projects-section">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <h2 className="section-title mb-0">Selected Work</h2>
            
            <div className="flex flex-wrap gap-2" data-testid="project-filters">
              {[
                { key: "all", label: "All" },
                { key: "architecture", label: "Architecture" },
                { key: "interior", label: "Interior" },
                { key: "furniture", label: "Furniture" },
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => handleFilter(filter.key)}
                  data-testid={`filter-${filter.key}`}
                  className={`filter-btn ${activeFilter === filter.key ? "active" : ""}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-32">
              <div className="spinner"></div>
            </div>
          ) : fetchError ? (
            <div className="text-center py-32" data-testid="projects-error">
              <p className="text-[#737373] mb-4">Unable to load projects</p>
              <p className="text-sm text-[#999] mb-6">{fetchError}</p>
              <button onClick={fetchProjects} className="btn-editorial">
                Try Again
              </button>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-32" data-testid="projects-empty">
              <p className="text-[#737373]">No projects found</p>
            </div>
          ) : (
            <div className="project-grid grid-cols-1 md:grid-cols-2">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  onClick={() => setSelectedProject(project)}
                  className={`project-item ${index === 0 ? "md:col-span-2" : ""}`}
                  data-testid={`project-card-${project.id}`}
                  style={{ aspectRatio: index === 0 ? "21/9" : "16/12" }}
                >
                  <img src={proxyImg(project.images[0])} alt={project.title} loading="lazy" />
                  <div className="project-overlay">
                    <span className="project-number">{String(index + 1).padStart(2, "0")}</span>
                    <h3 className="project-name">{project.title}</h3>
                    <p className="project-location">{project.location}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Philosophy Strip */}
      <section className="py-32 px-16 lg:px-40 bg-[#0A0A0A] text-white" data-testid="philosophy-section">
        <div className="max-w-screen-2xl mx-auto">
          <div className="max-w-4xl">
            <span className="label-caps text-white/50 block mb-8">Our Approach</span>
            <p className="about-text text-white/90">
              We believe great architecture emerges from deep understanding. Before 
              sketching a single line, we immerse ourselves in research—studying 
              user behaviors, environmental contexts, and cultural narratives.
            </p>
            <Link 
              to="/about" 
              className="inline-flex items-center gap-3 mt-12 text-white/60 hover:text-white transition-colors text-sm tracking-widest uppercase"
            >
              About the Studio
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-16 lg:px-40 bg-[#F5F5F3]" data-testid="cta-section">
        <div className="max-w-screen-2xl mx-auto text-center">
          <h2 className="font-['Syne'] font-semibold text-4xl lg:text-6xl mb-8">
            Let's Build Together
          </h2>
          <p className="text-[#737373] max-w-lg mx-auto mb-12">
            Have a project in mind? We'd love to hear about your vision and explore 
            how we can bring it to life.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="btn-editorial" data-testid="cta-contact-btn">
              Start a Conversation
              <ArrowUpRight size={16} />
            </Link>
            <Link to="/appointment" className="btn-outline" data-testid="cta-book-btn">
              Book Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* Project Detail */}
      <ProjectDetail
        project={selectedProject}
        open={!!selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </div>
  );
};
